import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {SavedProperty} from "../model/savedProperty";
import {Observable} from "rxjs/Observable";

@Injectable({
  providedIn: 'root'
})
export class SavedPropertyService {

  constructor(private httpClient:HttpClient) { }

    /**
     * Gets a saved property by its id
     *
     * @param id If of the saved property that should be gotten
     * @param headers Headers containing the loginId for a multi user
     * @return Observable with the found savedproperty for the specific id
     */
    public getSavedPropertyById(id: string, headers: any): Observable<SavedProperty> {
        return this.httpClient.get<SavedProperty>("/api/saved-properties/" + id, {headers: headers});
    }

    /**
     * Gets a map of all document names to their latest order dates for a specific saved property id.
     *
     * @param savedId If of a saved property for which the map should be gotten
     * @param headers Headers used for multi user checks in the back end
     */
    getDocumentsBySavedId(savedId: string, headers: any) {
        return this.httpClient.get("/api/saved-properties/" + savedId + "/documents", {headers: headers});
    }

    /**
     * Update a saved property by its id
     *
     * @param id Id of the saved property that should be updated
     * @param savedProperty Updated saved property data that should replace the existing one
     * @param files (Optional) Array of authority files associated to the saved property
     * @param headers Headers used for multi user checks in the back end
     * @return Observable with the updated SavedProperty
     */
     public updateSavedPropertyById(id, savedProperty: SavedProperty, files: any[], headers: any): Observable<SavedProperty> {
        const formData: FormData = this.getSavedPropertyAndFilesAsFormdata(savedProperty, files);
        return this.httpClient.put<SavedProperty>('/api/saved-properties/' + id, formData, {headers: headers});
    }

    /**
     * Add a new saved property
     * @param savedProperty SavedProperty data that should be added
     * @param files Array of authority files associated to the saved property
     * @param headers Headers used for multi user checks in the back end
     * @return Observable with the added SavedProperty
     */
    public addSavedProperty(savedProperty: SavedProperty, files: any[], headers: any): Observable<SavedProperty> {
        const formData: FormData = this.getSavedPropertyAndFilesAsFormdata(savedProperty, files);
        return this.httpClient.post<SavedProperty>('/api/saved-properties', formData, {headers: headers})
    }


    /**
     * Helper function that converts a saved property and an array of files into form data.
     * This is done to correctly pass the data via the API
     *
     * @param savedProperty Updated saved property data that should replace the existing one
     * @param files (Optional) Array of authority files associated to the saved property
     */
    public getSavedPropertyAndFilesAsFormdata(savedProperty: SavedProperty, files: any[]): FormData {
        const formData: FormData = new FormData();
        files.forEach(file => formData.append('files', file))
        formData.append('saved-property', new Blob([JSON.stringify(savedProperty)], {type: "application/json"}));
        return formData;
    }

    /**
     * Gets an authority file for a specific saved property.
     * The file is further specified by its s3Key.
     *
     * @param id Id of the saved property for which an authority should be gotten
     * @param s3Key s3Key of the specific authority file that should be gotten for the saved property
     * @param headers Headers used for multi user checks in the back end
     * @return Observable with an array buffer representing the gotten authority file
     */
    public getAuthorityByIdAndS3Key(id: string, s3Key: string, headers: any): Observable<ArrayBuffer> {
        let params = new HttpParams()
                .set("s3Key", s3Key);

        return this.httpClient.get("/api/saved-properties/" + id + "/authority-file", {
            params: params,
            responseType: "arraybuffer",
            headers: headers
        })
    }
}
